import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { replace } from 'connected-react-router';
import moment from 'moment-timezone';

import { Tab, Tabs } from '../../../../components/Tabs';
import PatientInfo from './PatientInfo';
import { getProgramsFromPatient, isAccessProgram } from '../../../../utils/cmsPrograms';
import EnrollmentHistory from './EnrollmentHistory';
import { PROGRAM_STATUS_SORT_ORDER, RPM_PATIENT_STATUS } from '../../../../constants';
import { getEnrollmentInfo } from '../Dashboard/PatientInfo';
import TableWidget from '../component/TableWidget';
import { OnboardStages } from '../../../../modals/OnboardPatientWizard/helpers';
import { openModalAction } from '../../../../actions/modal';
import { actions } from '../redux/actions';
import { hasPermission, PERMISSIONS } from '../../../../utils/userPermissions';
import { EXTERNAL_PROVIDER_TYPES } from '../../../../modals/EditExternalProviderModal';
import ExternalProviderDetails from './ExternalProviderDetails';

function PatientInfoTabs(props) {
  const { replacePath, patientId, enrollmentId, patientDetails, refreshTimestamp, editPatient, onRefresh, onRecordConsent } = props;
  const urlParams = new URLSearchParams(window.location.search);
  const tabFromUrl = urlParams.get('tab');

  const programs = useMemo(() => 
    getProgramsFromPatient(patientDetails, Object.values(RPM_PATIENT_STATUS).filter(status => status !== RPM_PATIENT_STATUS.notEnrolled)),
  [patientDetails]);

  const tabs = useMemo(() => {
    const sortedPrograms = [...programs].sort((a, b) => {
      const aOrder = PROGRAM_STATUS_SORT_ORDER[a.status] ?? Number.MAX_SAFE_INTEGER;
      const bOrder = PROGRAM_STATUS_SORT_ORDER[b.status] ?? Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder;
    });
    
    return [{id: 'info', label: 'Info'}].concat(sortedPrograms.map(program => ({id: program.enrollmentId, label: program.display(false, true)})));
  }, [programs]);

  const [selectedTab, setSelectedTab] = useState(tabFromUrl || 'info');

  useEffect(() => {
    if (selectedTab) {
      const newUrlParams = new URLSearchParams(window.location.search);
      newUrlParams.set('tab', selectedTab);
      replacePath(`${window.location.pathname}?${newUrlParams}`);
    }
  }, [selectedTab, replacePath]);

  const data = React.useMemo(() => {
    const program = programs?.find(p => p.enrollmentId === selectedTab);
    // Offered where the Enroll action is: consent is recorded in the enroll wizard.
    const canRecordConsent = onRecordConsent
      && program?.status === RPM_PATIENT_STATUS.verified
      && hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_ENROLL);
    const enrollmentInfo = getEnrollmentInfo(program, {
      onRecordConsent: canRecordConsent ? () => onRecordConsent(program.enrollmentId) : undefined,
    });
    if (enrollmentInfo) {
      return enrollmentInfo.map(item => [item?.name, item?. value]);
    }
    return [];
  }, [enrollmentId, programs, selectedTab, onRecordConsent]);
  
  const getButtons = (tabId) => {
    const program = programs?.find(p => p.enrollmentId === tabId);
    const isEditable = (program?.enrollmentEnd && moment().isBefore(program.enrollmentEnd) || !program?.enrollmentEnd)
        && program?.program === 'RPM' && hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_INFO_EDIT);
    if (isEditable) {
      return [
        {
          icon: 'edit',
          onClick: () => {
            editPatient(patientDetails, onRefresh, OnboardStages.editEnrollment, selectedTab);
          },
          id: `edit-${tabId}`,
        },
      ];
    }
    return undefined;
  };

  const tabArray = React.useMemo(() => tabs.map(s => {
    const program = programs.find(p => p.enrollmentId === s.id);
    return (
      <Tab label={s.label} key={s.id}>
        {s.id === 'info' ? (
          <PatientInfo
            patientDetails={patientDetails}
            patientId={patientId}
            enrollmentId={enrollmentId}
            refreshTimestamp={refreshTimestamp}
            onRefresh={onRefresh}
          />
        ) : (
          <>
            <div className="list-table">
              <div className="list-table-border with-wrapping">
                <TableWidget
                  title={program?.displayName}
                  data={s.id === selectedTab ? data : []}
                  firstColumnClassName="medical-history-col"
                  buttons={getButtons(s.id)}
                />
              </div>
            </div>
            {isAccessProgram(program?.program) && (
              <div className="list-table enrollment-provider-table">
                <div className="list-table-border with-wrapping">
                  <ExternalProviderDetails
                    provider={program.referringProvider}
                    providerType={EXTERNAL_PROVIDER_TYPES.referring}
                    patientId={patientDetails.masked_id}
                    enrollmentId={s.id}
                    onRefresh={onRefresh}
                    editable={hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_INFO_EDIT)}
                  />
                </div>
              </div>
            )}
            <EnrollmentHistory patientId={patientId} enrollmentId={program?.enrollmentId} />
          </>
        )}
      </Tab>
    );
  }), [data, enrollmentId, onRefresh, patientId, patientDetails, programs, refreshTimestamp, selectedTab, tabs]);

  const selectedTabIndex = Math.max(0, tabs.findIndex(t => t.id === selectedTab));

  return tabArray?.length ? (
    <Tabs
      defaultIndex={selectedTabIndex}
      onChange={i => setSelectedTab(tabs[i].id)}
    >
      {tabArray}
    </Tabs>
  ) : null;
}

PatientInfoTabs.propTypes = {
  replacePath: PropTypes.func.isRequired,
  patientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  enrollmentId: PropTypes.string,
  patientDetails: PropTypes.object.isRequired,
  refreshTimestamp: PropTypes.number,
  editPatient: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  onRecordConsent: PropTypes.func,
};

const mapDispatchToProps = dispatch => ({
  replacePath: url => dispatch(replace(url)),
  editPatient: (patient, onSuccess, stage, selectedEnrollmentId) =>
    dispatch(
      openModalAction('onboard-patient', {
        patientAction: actions.editPatient,
        onSuccess,
        patient,
        stage,
        selectedEnrollmentId,
      }),
    ),
});

export default connect(null, mapDispatchToProps)(PatientInfoTabs);
